import type { PortalUser } from '@/services/api/types';

/**
 * Minimal, dependency-free JWT reader for the SuiteAPI session token.
 *
 * SECURITY: This only base64url-decodes the (unverified) payload to read the
 * standard `exp` claim and the identity claims the Portal encodes (`sub`, `org`,
 * `brand` — see SuiteApiIdentity.GenerateJwt). It does NOT verify the signature:
 * client-side signature verification is meaningless (the secret would have to ship
 * to the browser). Authenticity is enforced server-side on every request — an
 * invalid/expired token is rejected with 401/403 and the shared unauthorized
 * handler redirects to /login. This reader only avoids priming an obviously-expired
 * token and reconstructs the display identity after a refresh.
 */

interface JwtClaims {
  sub?: string;
  org?: string;
  brand?: string;
  exp?: number;
}

function decodeBase64Url(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(padded)));
}

/** Decodes the JWT payload, or returns null when the token is malformed. */
export function decodeJwtClaims(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    return JSON.parse(decodeBase64Url(payload)) as JwtClaims;
  } catch {
    return null;
  }
}

/** True when the token carries an `exp` claim that is at or before `now` (seconds). */
export function isJwtExpired(claims: JwtClaims, nowSeconds: number = Date.now() / 1000): boolean {
  return typeof claims.exp === 'number' && claims.exp <= nowSeconds;
}

/**
 * Reconstructs the minimal {@link PortalUser} for a stored JWT session on refresh,
 * or returns null when the token is malformed or already expired.
 */
export function restoreUserFromJwt(token: string): PortalUser | null {
  const claims = decodeJwtClaims(token);
  if (!claims || isJwtExpired(claims)) {
    return null;
  }
  return {
    eMail: claims.sub ?? '',
    orgID: claims.org && claims.org.length > 0 ? claims.org : null,
  };
}
